import { zodResolver } from '@hookform/resolvers/zod';
import { PlusSquareIcon, XIcon } from '@phosphor-icons/react';
import { JSX, useEffect } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import colors from 'tailwindcss/colors';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';

import { ModelSizeFormSchema, ModelSizeFormValues } from '../validations';

interface ModelSizeFormProps {
  defaultValues?: ModelSizeFormValues;
  onSubmit: (data: ModelSizeFormValues) => Promise<void>;
  children: JSX.Element;
}

const defaultFormValues: ModelSizeFormValues = {
  value: '',
  sortOrder: 0,
  hints: [],
};

export function ModelSizeForm({
  onSubmit,
  children,
  defaultValues,
}: ModelSizeFormProps) {
  const form = useForm<ModelSizeFormValues>({
    resolver: zodResolver(ModelSizeFormSchema),
    defaultValues: defaultFormValues,
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'hints',
  });

  useEffect(() => {
    if (defaultValues) {
      form.reset(defaultValues);
    }
  }, [defaultValues]);

  const addHint = () => {
    append({ value: '', measure: '', sortOrder: 0 });
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        {children}

        <div className="flex flex-col gap-10 max-w-5xl">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <FormField
              control={form.control}
              name="value"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Size name</FormLabel>
                  <FormControl>
                    <Input placeholder="Size name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="sortOrder"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Order</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={0}
                      placeholder="Order"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="flex flex-col gap-6">
            <h3 className="text-xl font-semibold text-zinc-50">Hints</h3>

            {fields.map((item, index) => (
              <div
                key={item.id}
                className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_auto] gap-4 items-start"
              >
                <FormField
                  control={form.control}
                  name={`hints.${index}.value`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Value</FormLabel>
                      <FormControl>
                        <Input placeholder="Value" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={`hints.${index}.measure`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Measure</FormLabel>
                      <FormControl>
                        <Input placeholder="Measure" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={`hints.${index}.sortOrder`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Order</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={0}
                          placeholder="Order"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-11 w-11 mt-[32px]"
                  onClick={() => remove(index)}
                  data-testid={`remove-hint-button-${index}`}
                >
                  <XIcon size={20} color={colors.zinc[400]} />
                </Button>
              </div>
            ))}

            <div>
              <Button
                type="button"
                variant="outline"
                onClick={addHint}
                startIcon={<PlusSquareIcon />}
                data-testid="add-hint-button"
              >
                Add hint
              </Button>
            </div>
          </div>
        </div>
      </form>
    </Form>
  );
}

export default ModelSizeForm;
